// Swedish public holidays calculator (red days).
// We compute these client-side because there are only ~14/year and the
// algorithms are stable. No network calls needed.

import { formatLocalISO } from "./dates";

function easterSunday(year: number): Date {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * m + 114) / 31);
  const day = ((h + L - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}

function midsummerDay(year: number): Date {
  // Saturday between June 20 and June 26
  for (let day = 20; day <= 26; day++) {
    const d = new Date(year, 5, day);
    if (d.getDay() === 6) return d;
  }
  return new Date(year, 5, 24);
}

function midsummerEve(year: number): Date {
  return addDays(midsummerDay(year), -1);
}

function allSaintsDay(year: number): Date {
  // Saturday between October 31 and November 6
  for (let day = 31; day <= 36; day++) {
    const d = new Date(year, 9, day);
    if (d.getDay() === 6) return d;
  }
  return new Date(year, 10, 1);
}

export interface Holiday {
  date: string; // YYYY-MM-DD
  name: string;
  /** When true, this is "halv" (half day) - typically morning off, treated as full red day for our purposes. */
  halfDay?: boolean;
}

const cache = new Map<number, Holiday[]>();

export function holidaysForYear(year: number): Holiday[] {
  const cached = cache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const items: Holiday[] = [
    { date: formatLocalISO(new Date(year, 0, 1)), name: "Nyårsdagen" },
    { date: formatLocalISO(new Date(year, 0, 6)), name: "Trettondedag jul" },
    { date: formatLocalISO(addDays(easter, -2)), name: "Långfredagen" },
    { date: formatLocalISO(easter), name: "Påskdagen" },
    { date: formatLocalISO(addDays(easter, 1)), name: "Annandag påsk" },
    { date: formatLocalISO(new Date(year, 4, 1)), name: "Första maj" },
    { date: formatLocalISO(addDays(easter, 39)), name: "Kristi himmelfärds dag" },
    { date: formatLocalISO(addDays(easter, 49)), name: "Pingstdagen" },
    { date: formatLocalISO(new Date(year, 5, 6)), name: "Sveriges nationaldag" },
    { date: formatLocalISO(midsummerEve(year)), name: "Midsommarafton", halfDay: true },
    { date: formatLocalISO(midsummerDay(year)), name: "Midsommardagen" },
    { date: formatLocalISO(allSaintsDay(year)), name: "Alla helgons dag" },
    { date: formatLocalISO(new Date(year, 11, 24)), name: "Julafton", halfDay: true },
    { date: formatLocalISO(new Date(year, 11, 25)), name: "Juldagen" },
    { date: formatLocalISO(new Date(year, 11, 26)), name: "Annandag jul" },
    { date: formatLocalISO(new Date(year, 11, 31)), name: "Nyårsafton", halfDay: true },
  ];

  cache.set(year, items);
  return items;
}

export function holidayMapForYears(years: number[]): Map<string, Holiday> {
  const map = new Map<string, Holiday>();
  for (const y of years) {
    for (const h of holidaysForYear(y)) {
      map.set(h.date, h);
    }
  }
  return map;
}

export function isHoliday(d: Date): Holiday | null {
  const key = formatLocalISO(d);
  const list = holidaysForYear(d.getFullYear());
  return list.find((h) => h.date === key) ?? null;
}

/** Count working days (Mon-Fri, excluding Swedish public holidays) between two dates inclusive. */
export function countWorkdays(from: Date, to: Date): number {
  let count = 0;
  const cur = new Date(from);
  while (cur.getTime() <= to.getTime()) {
    const day = cur.getDay();
    if (day !== 0 && day !== 6 && !isHoliday(cur)) {
      count++;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/** Count total calendar days inclusive. */
export function countCalendarDays(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
}
